import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { BusinessAPI } from '../api/business.api'
import { UserAuth } from '../context/AuthContext'

interface UseInfiniteDigitalSummaryParams {
    businessId: number
    organizationId: number
    year: number
    month: number
    limit?: number
    source?: string
    operationType?: string
    bankAccountId?: number
}

export const useInfiniteDigitalSummary = ({
    businessId,
    organizationId,
    year,
    month,
    limit = 15,
    source,
    operationType,
    bankAccountId
}: UseInfiniteDigitalSummaryParams) => {
    const { isAuthenticated } = UserAuth()
    const businessApi = new BusinessAPI()

    return useInfiniteQuery({
        enabled: isAuthenticated && !!businessId && !!organizationId,
        queryKey: [
            QueryKeys.DIGITAL_PAYMENTS_SUMMARY,
            businessId,
            organizationId,
            year,
            month,
            limit,
            source,
            operationType,
            bankAccountId
        ],
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        queryFn: async ({ pageParam = 0 }: { pageParam?: number }) => {
            const beginDate = month > 0
                ? `${year}-${String(month).padStart(2, '0')}-01T00:00:00Z`
                : `${year}-01-01T00:00:00Z`

            const endDate = month > 0
                ? `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}T23:59:59Z`
                : `${year}-12-31T23:59:59Z`

            const params = {
                beginDate,
                endDate,
                limit,
                page: (pageParam || 0) + 1,
                source: source || undefined,
                operationType: operationType || undefined,
                bankAccountId: bankAccountId || undefined
            }

            const response = await businessApi.getDigitalPaymentsSummary(
                businessId,
                organizationId,
                params
            )

            const data = response.data.summary || []
            const total = response.data.total || data.length
            const hasNext = data.length === limit

            return {
                data,
                page: pageParam,
                hasNext,
                total
            }
        },
        getNextPageParam: (lastPage: any) => {
            return lastPage.hasNext ? lastPage.page + 1 : undefined
        },
        initialPageParam: 0
    })
}
